<template>
  <div class="perfil-view">
    <h2>Mi Perfil</h2>
    <div v-if="usuario" class="card">
      <div class="card-header">
        Información del Usuario
      </div>
      <div class="card-body">
        <p><strong>Nombre:</strong> {{ usuario.Nombre }}</p>
        <p><strong>ID de Usuario:</strong> {{ usuario.UsuarioId }}</p>
        <p><strong>Email:</strong> {{ usuario.Email }}</p>
      </div>
    </div>

    <div class="card mt-4 asistencia-card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-clock-history me-2"></i>Mi asistencia</span>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="asistenciaLoading" @click="cargarMiAsistencia">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>
      <div class="card-body">
        <div v-if="asistenciaLoading" class="text-muted">Consultando tu asistencia...</div>
        <div v-else-if="asistenciaError" class="alert alert-warning mb-0">{{ asistenciaError }}</div>
        <template v-else-if="miAsistencia && miAsistencia.EmpleadoVinculado">
          <div class="asistencia-resumen">
            <div>
              <span class="asistencia-label">Empleado</span>
              <strong>{{ miAsistencia.EmpleadoNombre }}</strong>
              <small>{{ miAsistencia.EmpleadoClave }}</small>
            </div>
            <div>
              <span class="asistencia-label">Turno vigente</span>
              <strong>{{ miAsistencia.TurnoNombre || 'Sin turno asignado' }}</strong>
              <small v-if="miAsistencia.HoraDesde">{{ horaTurno(miAsistencia.HoraDesde) }}–{{ horaTurno(miAsistencia.HoraHasta) }}</small>
            </div>
            <div>
              <span class="asistencia-label">Última checada</span>
              <strong>{{ miAsistencia.UltimoEventoClave || 'Sin checadas recientes' }}</strong>
              <small v-if="miAsistencia.UltimaChecada">{{ fechaHora(miAsistencia.UltimaChecada) }}</small>
            </div>
            <div>
              <span class="asistencia-label">Jornada</span>
              <strong>{{ miAsistencia.EstadoJornada || 'Sin jornada calculada' }}</strong>
              <small>{{ miAsistencia.MinutosTrabajados || 0 }} minutos trabajados</small>
            </div>
          </div>

          <div v-if="asistenciaSuccess" class="alert alert-success py-2 mt-3">{{ asistenciaSuccess }}</div>
          <button
            type="button"
            class="btn btn-lg w-100 mt-3"
            :class="miAsistencia.SiguienteEventoClave === 'ENTRADA' ? 'btn-success' : 'btn-danger'"
            :disabled="asistenciaSaving"
            @click="registrarMiAsistencia"
          >
            <i :class="miAsistencia.SiguienteEventoClave === 'ENTRADA' ? 'bi bi-box-arrow-in-right' : 'bi bi-box-arrow-right'"></i>
            {{ asistenciaSaving ? 'Registrando...' : miAsistencia.SiguienteAccion }}
          </button>

          <div v-if="miAsistencia.UltimasChecadas && miAsistencia.UltimasChecadas.length" class="mt-4">
            <h6>Checadas recientes</h6>
            <div v-for="item in miAsistencia.UltimasChecadas" :key="item.AsistenciaRegistroId" class="asistencia-checada">
              <span>{{ item.TipoEventoClave }}</span>
              <strong>{{ fechaHora(item.FechaHora) }}</strong>
              <small>{{ item.OrigenCaptura }}</small>
            </div>
          </div>
        </template>
        <div v-else class="alert alert-warning mb-0">
          {{ miAsistencia?.Mensaje || 'Tu usuario no está vinculado a un empleado.' }}
        </div>
      </div>
    </div>

    <div class="card mt-4">
      <div class="card-header">
        Cambiar Contraseña
      </div>
      <div class="card-body">
        <form @submit.prevent="cambiarPassword">
          <div v-if="passwordError" class="alert alert-danger py-2">
            {{ passwordError }}
          </div>
          <div v-if="passwordSuccess" class="alert alert-success py-2">
            {{ passwordSuccess }}
          </div>
          <div class="mb-3">
            <label for="currentPassword" class="form-label">Contraseña Actual</label>
            <input type="password" class="form-control" id="currentPassword" v-model="passwords.current" required autocomplete="current-password">
          </div>
          <div class="mb-3">
            <label for="newPassword" class="form-label">Nueva Contraseña</label>
            <input type="password" class="form-control" id="newPassword" v-model="passwords.new" required autocomplete="new-password">
          </div>
          <div class="mb-3">
            <label for="confirmPassword" class="form-label">Confirmar Nueva Contraseña</label>
            <input type="password" class="form-control" id="confirmPassword" v-model="passwords.confirm" required autocomplete="new-password">
          </div>
          <button type="submit" class="btn btn-primary" :disabled="savingPassword">
            {{ savingPassword ? 'Guardando...' : 'Cambiar Contraseña' }}
          </button>
        </form>
      </div>
    </div>

    <div class="card mt-4">
      <div class="card-header">
        Credenciales ERP externo
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Estos datos sobreescriben el usuario global configurado en la empresa para conectarse al ERP externo.
        </p>
        <form @submit.prevent="guardarCredencialesErp">
          <div v-if="erpError" class="alert alert-danger py-2">{{ erpError }}</div>
          <div v-if="erpSuccess" class="alert alert-success py-2">{{ erpSuccess }}</div>
          <div class="mb-3">
            <label for="erpUsuario" class="form-label">Usuario ERP</label>
            <input type="text" class="form-control" id="erpUsuario" v-model="erp.usuario" maxlength="80" placeholder="Usuario dentro del ERP" autocomplete="off">
          </div>
          <div class="mb-3">
            <label for="erpPassword" class="form-label">Password ERP</label>
            <input type="password" class="form-control" id="erpPassword" v-model="erp.password" maxlength="200" placeholder="Dejar vacío para mantener actual" autocomplete="new-password">
          </div>
          <div class="mb-3">
            <label for="erpPasswordConfirm" class="form-label">Confirmar Password ERP</label>
            <input type="password" class="form-control" id="erpPasswordConfirm" v-model="erp.passwordConfirm" maxlength="200" placeholder="Repita la nueva contraseña" autocomplete="new-password">
          </div>
          <button type="submit" class="btn btn-primary" :disabled="savingErp">
            {{ savingErp ? 'Guardando...' : 'Guardar credenciales ERP' }}
          </button>
        </form>
      </div>
    </div>

    <div class="card mt-4">
      <div class="card-header">
        Configuración de Tema
      </div>
      <div class="card-body">
        <p class="mb-3">Selecciona el tema de la aplicación:</p>
        <div class="theme-options">
          <div class="form-check mb-2">
            <input 
              class="form-check-input" 
              type="radio" 
              name="theme" 
              id="theme-blue" 
              value="blue" 
              v-model="selectedTheme"
              @change="cambiarTema"
            >
            <label class="form-check-label" for="theme-blue">
              <span class="theme-preview theme-blue"></span>
              Azul (Actual)
            </label>
          </div>
          <div class="form-check mb-2">
            <input 
              class="form-check-input" 
              type="radio" 
              name="theme" 
              id="theme-light" 
              value="light" 
              v-model="selectedTheme"
              @change="cambiarTema"
            >
            <label class="form-check-label" for="theme-light">
              <span class="theme-preview theme-light"></span>
              Claro
            </label>
          </div>
          <div class="form-check mb-2">
            <input 
              class="form-check-input" 
              type="radio" 
              name="theme" 
              id="theme-dark" 
              value="dark" 
              v-model="selectedTheme"
              @change="cambiarTema"
            >
            <label class="form-check-label" for="theme-dark">
              <span class="theme-preview theme-dark"></span>
              Oscuro
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService from '@/shared/services/auth';
import usuarioService from '@/modules/usuarios/usuario.service';
import asistenciaService from '@/modules/asistencia/asistencia.service';
import { useTheme } from '@/composables/useTheme';
import { useAccessControl } from '@/composables/useAccessControl';

export default {
  name: 'PerfilView',
  data() {
    return {
      usuario: null,
      passwords: {
        current: '',
        new: '',
        confirm: ''
      },
      savingPassword: false,
      passwordError: '',
      passwordSuccess: '',
      erp: {
        usuario: '',
        password: '',
        passwordConfirm: ''
      },
      savingErp: false,
      erpError: '',
      erpSuccess: '',
      miAsistencia: null,
      asistenciaLoading: false,
      asistenciaSaving: false,
      asistenciaError: '',
      asistenciaSuccess: ''
    };
  },
  
  setup() {
    // Mi Perfil requiere sesion, pero no permiso de menu.
    const accessControl = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    });
    
    const { currentTheme, cambiarTema } = useTheme()
    
    return {
      ...accessControl,
      currentTheme,
      cambiarTema
    }
  },
  created() {
    const userData = authService.getUserData();
    if (userData && userData._UsuarioMo) {
      this.usuario = userData._UsuarioMo;
      this.erp.usuario = userData._UsuarioMo.ErpUsuario || '';
    }
    this.cargarMiAsistencia();
  },
  methods: {
    async cargarMiAsistencia() {
      this.asistenciaLoading = true;
      this.asistenciaError = '';
      try {
        const result = await asistenciaService.miEstado();
        if (result && result.Number >= 0) {
          this.miAsistencia = result.OData || result.odata || null;
        } else {
          this.asistenciaError = result?.Message || 'No fue posible consultar tu asistencia.';
        }
      } catch (error) {
        this.asistenciaError = error?.response?.data?.Message || 'No fue posible consultar tu asistencia.';
      } finally {
        this.asistenciaLoading = false;
      }
    },

    async registrarMiAsistencia() {
      const accion = this.miAsistencia?.SiguienteAccion || 'Registrar asistencia';
      if (!window.confirm(`¿Confirmas la operación "${accion}"?`)) return;

      this.asistenciaSaving = true;
      this.asistenciaError = '';
      this.asistenciaSuccess = '';
      try {
        const clave = window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const result = await asistenciaService.miRegistrar({ ClaveSolicitud: clave });
        if (result && result.Number >= 0) {
          this.asistenciaSuccess = result.Message || 'Asistencia registrada correctamente.';
          this.miAsistencia = result.OData || result.odata || this.miAsistencia;
        } else {
          this.asistenciaError = result?.Message || 'No fue posible registrar la asistencia.';
        }
      } catch (error) {
        this.asistenciaError = error?.response?.data?.Message || 'No fue posible registrar la asistencia.';
      } finally {
        this.asistenciaSaving = false;
      }
    },

    fechaHora(value) {
      return value ? new Date(value).toLocaleString('es-MX') : '';
    },

    horaTurno(value) {
      return value ? String(value).slice(0, 5) : '--:--';
    },

    async cambiarPassword() {
      this.passwordError = '';
      this.passwordSuccess = '';

      if (!this.passwords.current || !this.passwords.new || !this.passwords.confirm) {
        this.passwordError = 'Captura la contrasena actual y la nueva contrasena.';
        return;
      }

      if (this.passwords.new !== this.passwords.confirm) {
        this.passwordError = 'Las nuevas contrasenas no coinciden.';
        return;
      }

      this.savingPassword = true;
      try {
        const result = await usuarioService.actualizarMiPassword({
          Password: this.passwords.current,
          NewPassword: this.passwords.new
        });

        if (result && result.Number >= 0) {
          this.passwordSuccess = result.Message || 'Contrasena actualizada correctamente.';
          this.passwords = { current: '', new: '', confirm: '' };
        } else {
          this.passwordError = (result && result.Message) || 'No fue posible actualizar la contrasena.';
        }
      } catch {
        this.passwordError = 'Error de conexion con el servidor.';
      } finally {
        this.savingPassword = false;
      }
    },
    
    handleThemeChange(theme) {
      this.cambiarTema(theme);
    },

    async guardarCredencialesErp() {
      this.erpError = '';
      this.erpSuccess = '';

      if (this.erp.password && this.erp.password !== this.erp.passwordConfirm) {
        this.erpError = 'Las contraseñas no coinciden.';
        return;
      }

      this.savingErp = true;
      try {
        const result = await usuarioService.actualizarMisCredencialesErp({
          ErpUsuario: this.erp.usuario,
          ErpPassword: this.erp.password
        });

        if (result && result.Number >= 0) {
          this.erpSuccess = result.Message || 'Credenciales ERP actualizadas correctamente.';
          this.erp.password = '';
          this.erp.passwordConfirm = '';
          if (this.usuario) this.usuario.ErpUsuario = this.erp.usuario;
        } else {
          this.erpError = (result && result.Message) || 'No fue posible actualizar las credenciales ERP.';
        }
      } catch {
        this.erpError = 'Error de conexión con el servidor.';
      } finally {
        this.savingErp = false;
      }
    }
  }
};
</script>

<style scoped>
.perfil-view {
  max-width: 600px;
  margin: auto;
}

.theme-options .form-check-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.theme-options .form-check-label:hover {
  background-color: #f8f9fa;
}

.theme-preview {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 10px;
  border: 2px solid #dee2e6;
}

.theme-preview.theme-blue {
  background: linear-gradient(45deg, #007bff, #0056b3);
  border-color: #007bff;
}

.theme-preview.theme-light {
  background: linear-gradient(45deg, #f8f9fa, #6c757d);
  border-color: #6c757d;
}

.theme-preview.theme-dark {
  background: linear-gradient(45deg, #212529, #0d6efd);
  border-color: #495057;
}

.form-check-input:checked + .form-check-label {
  background-color: #e3f2fd;
  font-weight: 500;
}

.form-check-input:checked + .form-check-label .theme-preview {
  box-shadow: 0 0 0 2px #007bff;
}

.asistencia-card {
  border-color: #bfdbfe;
}

.asistencia-resumen {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.asistencia-resumen > div {
  display: flex;
  flex-direction: column;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
}

.asistencia-label,
.asistencia-resumen small,
.asistencia-checada small {
  color: #64748b;
  font-size: 0.78rem;
}

.asistencia-checada {
  display: grid;
  grid-template-columns: 80px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.85rem;
}

@media (max-width: 575px) {
  .asistencia-resumen {
    grid-template-columns: 1fr;
  }

  .asistencia-checada {
    grid-template-columns: 70px 1fr;
  }

  .asistencia-checada small {
    display: none;
  }
}
</style>
